import {
  buildPracticeTrackRequestPayload,
  buildTimingSongPlan,
  refineTimingFromMelodyDuration,
  timingPlanHasBlockingWarnings,
  timingPlanNeedsAcknowledgement,
} from './timingSongPlanExtractor';
import { drumGuideOptionsFromTune } from './practiceTrackDrumGuide';
import { renderAbcToAudioBuffer } from './notationAudioExport';
import { encodeAudioBufferToWav } from './encodeAudioBufferToWav';
import {
  startLinkedCoverGeneration,
  startPracticeTrackGeneration,
} from './musicGenerationClient';
import { buildPracticeTrackMidiScore, midiScoreToBlob } from './practiceTrackMidiScore';
import { DEFAULT_RENDER_STYLE, shouldIncludeDrumGuide } from './practiceTrackStylePresets';
import {
  TASK_LINKED_COVER,
  TASK_PRACTICE_TRACK,
  defaultPresetForTask,
  formatAudioGenerationError,
  presetLabel,
} from './audioGenerationPresets';
import { enqueueAudioGenerationJob, bindAudioGenerationResolverJob, failAudioGenerationJob, updateAudioGenerationJobMessage } from './audioGenerationJobStore';
import { showAudioGenerationErrorToast, showAudioGenerationWaitingToast } from './audioGenerationToast';
import { getLinkSrcType } from './checkTuneLinkPlayback';
import {
  getRecording,
  isOwnedMediaLink,
  parseRecordingIdFromLinkUri,
  resolveRecordingLinkAudio,
  resolveRecordingLinkMidi,
} from './linkRecording';
import { isMidiFileName, isMidiOwnedMediaLink } from './midiFileUtils';
import { renderMidiBytesToWavBlob, renderMidiLinkToWavBlob } from './midiRenderClient';
import { noteLinesHaveRealMelody } from './timedImportFinalizer';
import { resolveResolverAccessToken } from './resolverAccessToken';
import { getActiveResolverAccessToken } from './mediaResolverHealthStore';

function resolveGenerationAccessToken(token) {
  return resolveResolverAccessToken(token) || getActiveResolverAccessToken() || '';
}

export function tuneHasRenderableMelody(tune) {
  if (!tune || !tune.voices) return false;
  return Object.values(tune.voices).some(function(voice) {
    return noteLinesHaveRealMelody(voice && voice.notes);
  });
}

export function tuneAbcFromContext(tune, tunebook) {
  if (!tune || !tunebook || !tunebook.abcTools) return '';
  return tunebook.abcTools.json2abc(tune);
}

export function getPracticeTrackPlan(tune, tunebook, abcjsParser) {
  const abc = tuneAbcFromContext(tune, tunebook);
  if (!abc) return null;
  try {
    return buildTimingSongPlan(tune, abc, {
      abcTools: tunebook && tunebook.abcTools ? tunebook.abcTools : null,
      abcjsParser: abcjsParser,
      tunebook: tunebook,
      forPracticeTrack: true,
    });
  } catch (err) {
    return null;
  }
}

export function hasPracticeTrackMidiData(tune, tunebook, abcjsParser) {
  if (!tuneHasRenderableMelody(tune)) return false;
  const plan = getPracticeTrackPlan(tune, tunebook, abcjsParser);
  if (!plan) return false;
  try {
    buildPracticeTrackMidiScore(tune, tunebook, plan);
    return true;
  } catch (err) {
    return false;
  }
}

export function linkSupportsAudioCover(link, isYoutubeLink) {
  if (!link || !link.link || !String(link.link).trim()) return false;
  const srcType = getLinkSrcType(link, isYoutubeLink);
  return srcType === 'audio'
    || srcType === 'recording'
    || srcType === 'youtube'
    || srcType === 'midifile';
}

export function defaultCoverStylePrompt(tune, plan) {
  const parts = [];
  const musical = plan && plan.musical ? plan.musical : {};
  const timing = plan && plan.timing ? plan.timing : {};
  const tempo = Math.round(parseFloat(timing.tempoBpm || musical.tempoBpm) || 0);
  if (tempo > 0) parts.push(tempo + ' BPM');
  const meter = musical.meter || timing.meter;
  if (meter) parts.push(String(meter));
  if (tune && tune.rhythm) parts.push(String(tune.rhythm) + ' style');
  if (tune && tune.genre) parts.push(String(tune.genre));
  const style = parts.join(', ') || 'same genre as the source';
  return (
    'Faithful cover preserving the exact melody and song structure of the source. Style: '
    + style
  );
}

export function linkedCoverSourceFilename(link, tune, srcType, blob) {
  const base = String((link && link.title) || (tune && tune.name) || 'source')
    .trim()
    .replace(/[^\w.-]+/g, '_') || 'source';
  if (srcType === 'midifile') return base + '.wav';
  if (srcType === 'recording') return base + '.mp3';
  if (blob && blob.type) {
    if (blob.type.indexOf('wav') !== -1) return base + '.wav';
    if (blob.type.indexOf('mpeg') !== -1 || blob.type.indexOf('mp3') !== -1) return base + '.mp3';
  }
  return base + '.wav';
}

async function ownedMediaLinkIsMidi(link) {
  if (isMidiOwnedMediaLink(link) || isMidiFileName(link && link.title)) return true;
  if (!isOwnedMediaLink(link)) return false;
  const recordingId = (link && link.recordingId) || parseRecordingIdFromLinkUri(link && link.link);
  if (!recordingId) return false;
  const recording = await getRecording(recordingId);
  return !!(recording && (recording.mediaKind === 'midi' || recording.type === 'audio/midi'));
}

async function resolveOwnedMidiToWavBlob(link, tuneId, linkIndex, options) {
  const opts = options || {};
  const resolved = await resolveRecordingLinkMidi(link, tuneId, linkIndex, {
    accessToken: opts.token,
    driveApi: opts.driveApi,
    forPlayback: true,
  });
  const baseName = (link && link.title ? String(link.title).trim() : '') || 'source';
  return renderMidiBytesToWavBlob(resolved.arrayBuffer, {
    token: opts.token,
    filename: baseName.replace(/\.(mid|midi)$/i, '') + '.mid',
  });
}

export async function resolveLinkedCoverSourceBlob(options) {
  const opts = options || {};
  const link = opts.link;
  const srcType = opts.srcType;
  if (!link || !srcType) return null;

  const tuneId = opts.tuneId;
  const linkIndex = opts.linkIndex;

  if (srcType === 'midifile') {
    if (tuneId == null || linkIndex == null) {
      throw new Error('Missing tune context for MIDI regeneration.');
    }
    return renderMidiLinkToWavBlob(link, tuneId, linkIndex, {
      token: opts.token,
      driveApi: opts.driveApi,
      isYoutubeLink: opts.isYoutubeLink,
    });
  }

  if (srcType === 'recording') {
    if (tuneId == null || linkIndex == null) {
      throw new Error('Missing tune context for recording regeneration.');
    }
    const accessToken = resolveGenerationAccessToken(opts.token);
    if (!accessToken && !(link && link.googleId)) {
      // Local-only recordings can still resolve from IndexedDB without a token.
      // Drive-backed recordings need sign-in; missing both fails later with a clear error.
    }
    if (await ownedMediaLinkIsMidi(link)) {
      return resolveOwnedMidiToWavBlob(link, tuneId, linkIndex, opts);
    }
    try {
      const resolved = await resolveRecordingLinkAudio(link, tuneId, linkIndex, {
        accessToken: accessToken || opts.token,
        driveApi: opts.driveApi,
        forPlayback: true,
      });
      if (!resolved || !resolved.blob) {
        throw new Error('Recording is not available for regeneration.');
      }
      return resolved.blob;
    } catch (audioErr) {
      try {
        return await resolveOwnedMidiToWavBlob(link, tuneId, linkIndex, Object.assign({}, opts, {
          token: accessToken || opts.token,
        }));
      } catch (midiErr) {
        throw audioErr;
      }
    }
  }

  return null;
}

function getTuneContextFactory(tune, tunebook, onTuneChange, forceRefresh) {
  return function getTuneContext(tuneId) {
    if (!tune || String(tune.id) !== String(tuneId)) return null;
    return {
      tune: tune,
      tunebook: tunebook,
      onTuneChange: onTuneChange,
      forceRefresh: forceRefresh,
    };
  };
}

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise(function(_resolve, reject) {
      setTimeout(function() {
        reject(new Error(message || 'Timed out'));
      }, ms);
    }),
  ]);
}

/**
 * Run wizard generation after modals close (survives unmount).
 * Enqueues a local job immediately so Background Jobs lists it, then prepares
 * MIDI/WAV and starts the resolver job.
 */
export async function runAudioGenerationFromWizard(spec) {
  const opts = spec || {};
  const tune = opts.tune;
  const tunebook = opts.tunebook;
  const token = opts.token;
  const taskId = opts.taskId || TASK_PRACTICE_TRACK;
  const presetId = opts.presetId || defaultPresetForTask(taskId);
  const presetLabelText = opts.presetLabel || presetLabel(presetId);
  const onTuneChange = opts.onTuneChange;
  const forceRefresh = opts.forceRefresh;
  const getTuneContext = getTuneContextFactory(tune, tunebook, onTuneChange, forceRefresh);

  const localJobId = enqueueAudioGenerationJob({
    tuneId: tune && tune.id,
    tuneName: (tune && tune.name) || '',
    taskId: taskId,
    presetId: presetId,
    presetLabel: presetLabelText,
    accessToken: token,
    tune: tune,
    tunebook: tunebook,
    onTuneChange: onTuneChange,
    forceRefresh: forceRefresh,
    getTuneContext: getTuneContext,
    message: 'Preparing…',
  });

  try {
    if (taskId === TASK_PRACTICE_TRACK) {
      const plan = opts.plan;
      if (!plan) {
        throw new Error('Could not build timing plan for this tune.');
      }
      const abc = opts.abc || tuneAbcFromContext(tune, tunebook);
      const melodySource = opts.melodySource || 'notation_midi';
      const renderStyle = opts.renderStyle || DEFAULT_RENDER_STYLE;
      const backingPrompt = opts.backingPrompt;
      const includeDrumGuide = opts.includeDrumGuide;

      updateAudioGenerationJobMessage(localJobId, 'Building MIDI score…', 'preparing');
      const midiScore = buildPracticeTrackMidiScore(tune, tunebook, plan);
      const melodyAbc = melodySource === 'notation_midi' ? midiScore.abc : abc;
      updateAudioGenerationJobMessage(localJobId, 'Rendering melody…', 'preparing');
      const buffer = await withTimeout(
        renderAbcToAudioBuffer(melodyAbc, {
          chordsOff: melodySource !== 'notation_midi',
          tune: tune,
        }),
        120000,
        'Melody render timed out. Check your network (soundfonts) and try again.'
      );
      const melody = encodeAudioBufferToWav(buffer);
      const activePlan = refineTimingFromMelodyDuration(plan, buffer.duration);
      const payload = buildPracticeTrackRequestPayload(activePlan, drumGuideOptionsFromTune(tune, activePlan, {
        backingPrompt: renderStyle === 'custom' ? backingPrompt : undefined,
        renderStyle: renderStyle,
        melodySource: melodySource,
        includeChordLayer: false,
        includeDrumGuide: includeDrumGuide != null
          ? !!includeDrumGuide && shouldIncludeDrumGuide(renderStyle, activePlan)
          : shouldIncludeDrumGuide(renderStyle, activePlan),
        guideAudioConditioning: true,
        includeStyleMelodyStem: false,
        acknowledgeBarEstimate: activePlan.timing.source !== 'bar-estimate',
        presetId: presetId,
      }));

      updateAudioGenerationJobMessage(localJobId, 'Starting generation…', 'preparing');
      const started = await withTimeout(
        startPracticeTrackGeneration(payload, melody, {
          token: token,
          presetId: presetId,
          scoreBlob: midiScoreToBlob(midiScore.midiBytes),
          onWaiting: function() {
            updateAudioGenerationJobMessage(localJobId, 'Waiting for GPU…', 'waiting_gpu');
            showAudioGenerationWaitingToast('Waiting for GPU…');
          },
        }),
        180000,
        'Could not reach the audio generation service. Check the resolver and try again.'
      );
      bindAudioGenerationResolverJob(localJobId, started.jobId, {
        accessToken: token,
        getTuneContext: getTuneContext,
      });
      return started.jobId;
    }

    const linkEntry = opts.linkEntry;
    if (!linkEntry || !linkEntry.link) {
      throw new Error('Selected link is no longer available');
    }
    const requestPayload = {
      taskId: TASK_LINKED_COVER,
      presetId: presetId,
      sourceUrl: linkEntry.link.link,
      sourceType: linkEntry.srcType,
      stylePrompt: String(opts.coverStylePrompt || '').trim(),
      lyrics: String(opts.coverLyrics || '').trim(),
      title: linkEntry.link.title || tune.name || '',
      startAt: linkEntry.srcType === 'midifile' ? 0 : (parseFloat(linkEntry.link.startAt) || 0),
      endAt: linkEntry.srcType === 'midifile' ? 0 : (parseFloat(linkEntry.link.endAt) || 0),
    };
    updateAudioGenerationJobMessage(localJobId, 'Starting cover…', 'preparing');
    const started = await withTimeout(
      startLinkedCoverGeneration(requestPayload, {
        token: token,
        presetId: presetId,
        onWaiting: function() {
          updateAudioGenerationJobMessage(localJobId, 'Waiting for GPU…', 'waiting_gpu');
          showAudioGenerationWaitingToast('Waiting for GPU…');
        },
      }),
      180000,
      'Could not reach the audio generation service. Check the resolver and try again.'
    );
    bindAudioGenerationResolverJob(localJobId, started.jobId, {
      accessToken: token,
      getTuneContext: getTuneContext,
    });
    return started.jobId;
  } catch (err) {
    const message = err && err.message ? err.message : 'Could not start audio generation.';
    failAudioGenerationJob(localJobId, message);
    throw err;
  }
}

export function validateAudioGenerationWizard(spec) {
  const opts = spec || {};
  const taskId = opts.taskId || TASK_PRACTICE_TRACK;
  if (taskId === TASK_PRACTICE_TRACK) {
    if (!opts.plan) {
      return 'Could not build timing plan for this tune.';
    }
    if (timingPlanHasBlockingWarnings(opts.plan) && !window.confirm(
      'This tune has notation structure errors. Generate anyway?'
    )) {
      return null;
    }
    if (timingPlanNeedsAcknowledgement(opts.plan) && !opts.ackBarEstimate) {
      return 'Timing is estimated from bar count. Check the acknowledgement box to continue.';
    }
    return '';
  }
  if (opts.selectedLinkIndex === null || opts.selectedLinkIndex === undefined) {
    return 'Select a linked recording.';
  }
  if (!String(opts.coverStylePrompt || '').trim()) {
    return 'Enter a style prompt for the cover.';
  }
  return '';
}

export function startAudioGenerationFromWizard(spec) {
  const validationError = validateAudioGenerationWizard(spec);
  if (validationError === null) {
    return Promise.resolve(null);
  }
  if (validationError) {
    return Promise.reject(new Error(validationError));
  }
  return runAudioGenerationFromWizard(spec).catch(function(err) {
    const message = formatAudioGenerationError(err && err.message ? err.message : 'Could not start audio generation.');
    showAudioGenerationErrorToast(message);
    throw err;
  });
}

export async function enqueuePracticeTrackJob(options) {
  const opts = options || {};
  const tune = opts.tune;
  const tunebook = opts.tunebook;
  const abcjsParser = opts.abcjsParser;
  const token = opts.token;
  const onTuneChange = opts.onTuneChange;
  const forceRefresh = opts.forceRefresh;
  const presetId = opts.presetId || defaultPresetForTask(TASK_PRACTICE_TRACK);

  const abc = tuneAbcFromContext(tune, tunebook);
  const plan = getPracticeTrackPlan(tune, tunebook, abcjsParser);
  if (!plan) {
    throw new Error('Could not build timing plan for this tune.');
  }
  if (timingPlanHasBlockingWarnings(plan) && !window.confirm(
    'This tune has notation structure errors. Generate anyway?'
  )) {
    return null;
  }

  const renderStyle = plan.renderStyle || DEFAULT_RENDER_STYLE;
  const midiScore = buildPracticeTrackMidiScore(tune, tunebook, plan);
  const buffer = await renderAbcToAudioBuffer(midiScore.abc, {
    chordsOff: false,
    tune: tune,
  });
  const melody = encodeAudioBufferToWav(buffer);
  const activePlan = refineTimingFromMelodyDuration(plan, buffer.duration);
  const payload = buildPracticeTrackRequestPayload(activePlan, drumGuideOptionsFromTune(tune, activePlan, {
    renderStyle: renderStyle,
    melodySource: 'notation_midi',
    includeChordLayer: false,
    includeDrumGuide: shouldIncludeDrumGuide(renderStyle, activePlan),
    guideAudioConditioning: true,
    includeStyleMelodyStem: false,
    acknowledgeBarEstimate: true,
    presetId: presetId,
  }));
  const started = await startPracticeTrackGeneration(payload, melody, {
    token: token,
    presetId: presetId,
    scoreBlob: midiScoreToBlob(midiScore.midiBytes),
  });

  const getTuneContext = getTuneContextFactory(tune, tunebook, onTuneChange, forceRefresh);
  enqueueAudioGenerationJob({
    tuneId: tune.id,
    tuneName: tune.name || '',
    taskId: TASK_PRACTICE_TRACK,
    presetId: presetId,
    presetLabel: presetLabel(presetId),
    resolverJobId: started.jobId,
    accessToken: token,
    tune: tune,
    tunebook: tunebook,
    onTuneChange: onTuneChange,
    forceRefresh: forceRefresh,
    getTuneContext: getTuneContext,
  });

  return started.jobId;
}

export async function enqueueLinkedCoverJob(options) {
  const opts = options || {};
  const tune = opts.tune;
  const tunebook = opts.tunebook;
  const abcjsParser = opts.abcjsParser;
  const token = resolveGenerationAccessToken(opts.token) || opts.token;
  const onTuneChange = opts.onTuneChange;
  const forceRefresh = opts.forceRefresh;
  const link = opts.link;
  const linkIndex = opts.linkIndex;
  const presetId = opts.presetId || defaultPresetForTask(TASK_LINKED_COVER);
  const isYoutubeLink = tunebook && tunebook.utils && tunebook.utils.isYoutubeLink;
  const srcType = getLinkSrcType(link, isYoutubeLink);

  if (!linkSupportsAudioCover(link, isYoutubeLink)) {
    throw new Error('This link cannot be used as an audio source for regeneration.');
  }

  const plan = getPracticeTrackPlan(tune, tunebook, abcjsParser);
  const stylePrompt = (opts.stylePrompt || defaultCoverStylePrompt(tune, plan)).trim();
  if (!stylePrompt) {
    throw new Error('Could not build a style prompt for this cover.');
  }

  const tuneId = opts.tuneId || tune.id;
  let sourceBlob = opts.sourceBlob || null;
  if (!sourceBlob) {
    sourceBlob = await resolveLinkedCoverSourceBlob({
      link: link,
      srcType: srcType,
      tuneId: tuneId,
      linkIndex: linkIndex,
      token: token,
      driveApi: opts.driveApi,
      isYoutubeLink: isYoutubeLink,
    });
  }

  const requestPayload = {
    taskId: TASK_LINKED_COVER,
    presetId: presetId,
    sourceUrl: link.link,
    sourceType: srcType === 'midifile' ? 'midifile' : srcType,
    stylePrompt: stylePrompt,
    lyrics: (opts.lyrics || '').trim(),
    title: link.title || tune.name || '',
    startAt: parseFloat(link.startAt) || 0,
    endAt: parseFloat(link.endAt) || 0,
  };
  const started = await startLinkedCoverGeneration(requestPayload, {
    token: token,
    presetId: presetId,
    sourceBlob: sourceBlob,
    sourceFilename: linkedCoverSourceFilename(link, tune, srcType, sourceBlob),
  });

  const getTuneContext = getTuneContextFactory(tune, tunebook, onTuneChange, forceRefresh);
  enqueueAudioGenerationJob({
    tuneId: tune.id,
    tuneName: tune.name || '',
    taskId: TASK_LINKED_COVER,
    presetId: presetId,
    presetLabel: presetLabel(presetId),
    resolverJobId: started.jobId,
    accessToken: token,
    tune: tune,
    tunebook: tunebook,
    onTuneChange: onTuneChange,
    forceRefresh: forceRefresh,
    getTuneContext: getTuneContext,
  });

  return started.jobId;
}
